export type TRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type TRectBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type TFiniteRectTuple = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type TPaddingBoundsMode = "clip-padded-edges" | "preserve-padded-size";

export const clampToRange = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const parseFiniteRectTuple = (
  value: unknown,
  { reorder = false }: { reorder?: boolean } = {},
): TFiniteRectTuple | null => {
  if (!Array.isArray(value) || value.length < 4) {
    return null;
  }

  const [rawX1, rawY1, rawX2, rawY2] = value.slice(0, 4).map(Number);
  if (
    !Number.isFinite(rawX1) ||
    !Number.isFinite(rawY1) ||
    !Number.isFinite(rawX2) ||
    !Number.isFinite(rawY2)
  ) {
    return null;
  }

  if (!reorder) {
    return { x1: rawX1, y1: rawY1, x2: rawX2, y2: rawY2 };
  }

  return {
    x1: Math.min(rawX1, rawX2),
    y1: Math.min(rawY1, rawY2),
    x2: Math.max(rawX1, rawX2),
    y2: Math.max(rawY1, rawY2),
  };
};

export const padAndBoundRect = ({
  rect,
  bounds,
  paddingRatio,
  minimumPadding,
  minimumWidth,
  minimumHeight,
  mode,
}: {
  rect: TRect;
  bounds: TRectBounds;
  paddingRatio: number;
  minimumPadding: number;
  minimumWidth: number;
  minimumHeight: number;
  mode: TPaddingBoundsMode;
}): TRect | null => {
  const paddingX = Math.max(minimumPadding, rect.width * paddingRatio);
  const paddingY = Math.max(minimumPadding, rect.height * paddingRatio);
  const paddedLeft = rect.left - paddingX;
  const paddedTop = rect.top - paddingY;
  const paddedWidth = rect.width + paddingX * 2;
  const paddedHeight = rect.height + paddingY * 2;
  const left = clampToRange(paddedLeft, bounds.minX, bounds.maxX);
  const top = clampToRange(paddedTop, bounds.minY, bounds.maxY);

  if (mode === "preserve-padded-size") {
    const width = Math.min(
      Math.max(minimumWidth, paddedWidth),
      bounds.maxX - left,
    );
    const height = Math.min(
      Math.max(minimumHeight, paddedHeight),
      bounds.maxY - top,
    );

    return width > 0 && height > 0 ? { left, top, width, height } : null;
  }

  const right = clampToRange(
    paddedLeft + paddedWidth,
    bounds.minX,
    bounds.maxX,
  );
  const bottom = clampToRange(
    paddedTop + paddedHeight,
    bounds.minY,
    bounds.maxY,
  );
  const width = Math.min(
    Math.max(minimumWidth, right - left),
    bounds.maxX - left,
  );
  const height = Math.min(
    Math.max(minimumHeight, bottom - top),
    bounds.maxY - top,
  );

  return width > 0 && height > 0 ? { left, top, width, height } : null;
};
